import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { baseURL } from "../backend_url";

import {
  Card,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Space,
  Typography,
  Checkbox,
  Button,
  Alert,
  Progress,
  message,
  Table,
  Tag,
  Statistic,
  Divider,
  Input,
  Tabs,
  Tooltip,
  Empty,
  Breadcrumb,
  Badge,
  Segmented,
  Grid,
} from "antd";

import {
  SearchOutlined,
  ClearOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadAccessibleCompanies, readSessionUser } from '../accountAccess';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

export default function RateAutomationInstantEnhanced() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [progressDuration, setProgressDuration] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);

  const [companyDetail, setCompanyDetail] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");

  const [locationDetail, setLocationDetail] = useState([]);
  const [locationName, setLocationName] = useState("");
  const [locationId, setLocationId] = useState("");

  const [loggedInUserDetail, setLoggedInUserDetail] = useState({});

  const [pickupTime, setPickupTime] = useState("");
  const [rateSource, setRateSource] = useState("Expedia");
  const [residency, setResidency] = useState("US");

  const [dateRange, setDateRange] = useState([moment(), moment()]);
  const [calendarValue, setCalendarValue] = useState(null);

  const [lorMap, setLorMap] = useState({
    "1": false,
    "2": false,
    "3": false,
    "4": false,
    "5": false,
    "6": false,
    Weekly: false,
    Monthly: false,
  });

  const [results, setResults] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterDate, setFilterDate] = useState(null);
  const [filterLor, setFilterLor] = useState(null);
  const [filterCarClass, setFilterCarClass] = useState(null);
  const [activeTab, setActiveTab] = useState("table");

  const pagePadding = isMobile ? "16px 10px" : isTablet ? "24px 14px" : "32px 16px";
  const cardPadding = isMobile ? 14 : 24;
  const titleLevel = isMobile ? 3 : 2;
  const buttonBlock = isMobile;

  const hours = useMemo(
    () => [
      "00:00", "00:30", "01:00", "01:30", "02:00", "02:30", "03:00", "03:30",
      "04:00", "04:30", "05:00", "05:30", "06:00", "06:30", "07:00", "07:30",
      "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
      "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
      "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
      "20:00", "20:30", "21:00", "21:30", "22:00", "22:30", "23:00", "23:30", "23:59",
    ],
    []
  );

  const rateSourceOptions = useMemo(() => {
    const base = [
      { value: "Expedia", label: "Expedia" },
      { value: "Priceline", label: "Priceline" },
      { value: "DiscoverCars", label: "DiscoverCars" },
    ];
    return base;
  }, []);

  const residencyOptions = useMemo(
    () => [
      { value: "US", label: "US" },
      { value: "AF", label: "Afghanistan" },
      { value: "AX", label: "Åland Islands" },
      { value: "AL", label: "Albania" },
      { value: "DZ", label: "Algeria" },
    ],
    []
  );

  const disabledDate = (current) => {
    if (!current) return false;
    if (current.isBefore(moment().startOf("day"))) return true;

    const start = calendarValue?.[0];
    const end = calendarValue?.[1];

    if (start && !end) {
      const min = start.clone().startOf("day");
      const max = start.clone().add(6, "days").endOf("day");
      return current.isBefore(min) || current.isAfter(max);
    }

    return false;
  };

  const normalizeResponseData = (rawData) => {
    console.log("🔍 Raw Response Data:", rawData);

    let dataArray = [];

    if (Array.isArray(rawData)) {
      dataArray = rawData;
    } else if (rawData?.data && Array.isArray(rawData.data)) {
      dataArray = rawData.data;
    } else if (rawData?.results && Array.isArray(rawData.results)) {
      dataArray = rawData.results;
    } else if (rawData?.items && Array.isArray(rawData.items)) {
      dataArray = rawData.items;
    } else if (rawData?.res && Array.isArray(rawData.res)) {
      dataArray = rawData.res;
    } else if (rawData?.response && Array.isArray(rawData.response)) {
      dataArray = rawData.response;
    } else if (rawData?.data?.results && Array.isArray(rawData.data.results)) {
      dataArray = rawData.data.results;
    } else if (rawData?.data?.res && Array.isArray(rawData.data.res)) {
      dataArray = rawData.data.res;
    } else if (rawData?.pStartDate && Array.isArray(rawData.pStartDate)) {
      dataArray = rawData.pStartDate.map((date, i) => ({
        pickup_date: date,
        lor: rawData.pLor?.[i],
        "Car Class": rawData.pSippCode?.[i],
        "OTA Displaying rate": rawData.pCompetitorPrice?.[i],
        "Rate Request Rate": rawData.pOurPrice?.[i],
        "Difference %": rawData.pRangeValue?.[i],
      }));
    } else if (rawData?.paginatedData && Array.isArray(rawData.paginatedData)) {
      dataArray = rawData.paginatedData;
    } else if (rawData && typeof rawData === "object") {
      const keys = Object.keys(rawData);
      for (const key of keys) {
        if (Array.isArray(rawData[key])) {
          dataArray = rawData[key];
          break;
        }
      }
    }

    console.log("✅ Normalized Data Array:", dataArray);
    return Array.isArray(dataArray) ? dataArray : [];
  };

  const formatLorLabel = (value) => {
    if (value === undefined || value === null || value === "") return "-";

    const num = Number(value);

    if (Number.isFinite(num)) {
      if (num === 7) return "Weekly";
      if (num > 26) return "Monthly";
      return String(num);
    }

    const lower = String(value).toLowerCase();
    if (lower === "weekly") return "Weekly";
    if (lower === "monthly") return "Monthly";

    return String(value);
  };

  const getLorSortValue = (value) => {
    if (value === undefined || value === null || value === "") return 0;

    const lower = String(value).toLowerCase();

    if (lower === "weekly") return 7;
    if (lower === "monthly") return 28;

    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const normalizeRow = (row, index) => {
    const getFieldValue = (rowObj, ...possibleKeys) => {
      for (const key of possibleKeys) {
        if (rowObj?.[key] !== undefined && rowObj?.[key] !== null) {
          return rowObj[key];
        }
      }
      return undefined;
    };

    const toText = (value) => {
      if (value === undefined || value === null || value === "") return "-";

      if (Array.isArray(value)) {
        return value
          .map((item) => {
            if (item === undefined || item === null) return "";
            if (typeof item === "object") {
              try {
                return JSON.stringify(item);
              } catch {
                return String(item);
              }
            }
            return String(item);
          })
          .join(", ");
      }

      if (typeof value === "object") {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }

      return String(value);
    };

    const toNumber = (value) => {
      if (value === undefined || value === null || value === "") return 0;

      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }

      if (typeof value === "string") {
        const cleaned = value.replace(/[$,%\s,]/g, "");
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      if (Array.isArray(value)) {
        return toNumber(value[0]);
      }

      if (typeof value === "object") {
        const primitiveCandidate =
          value?.value ??
          value?.amount ??
          value?.price ??
          value?.rate ??
          value?.percentage ??
          value?.percent;

        return toNumber(primitiveCandidate);
      }

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return {
      key: `row-${index}`,
      pickup_date: toText(
        getFieldValue(row, "pickup_date", "pickupDate", "pStartDate", "date", "startDate")
      ),
      lor: formatLorLabel(
        getFieldValue(row, "lor", "LOR", "pLor", "lengthOfRental", "rental_days")
      ),
      carClass: toText(
        getFieldValue(row, "Car Class", "carClass", "pSippCode", "sippCode", "SIPP_Code", "car_class")
      ),
      expediaRate: toNumber(
        getFieldValue(
          row,
          "Expedia rate",
          "OTA Displaying rate",
          "expediaRate",
          "pCompetitorPrice",
          "expedia_price",
          "competitor_rate"
        )
      ),
      routesRate: toNumber(
        getFieldValue(row, "Rate Request Rate", "routesRate", "pOurPrice", "our_price", "routes_rate", "rate_value")
      ),
      higherPct: toNumber(
        getFieldValue(row, "Difference %", "higherPct", "pRangeValue", "difference_percent", "percentage_higher", "variance")
      ),
      raw: row,
    };
  };

  useEffect(() => {
    const { user, error } = readSessionUser();

    if (error) {
      setLoggedInUserDetail({});
      setCompanyDetail([]);
      message.error(error);
      return;
    }

    setLoggedInUserDetail(user);
    loadAccessibleCompanies(baseURL, user, "RateUpdate_Account Access")
      .then(setCompanyDetail)
      .catch((loadError) => {
        setCompanyDetail([]);
        message.error(loadError.message || "Accounts could not be loaded.");
      });
  }, []);

  useEffect(() => {
    if (!companyName || companyDetail.length === 0) return;

    const company = companyDetail.find((c) => c.company_name === companyName);
    if (!company) return;

    const tempCompanyId = company._id;
    setCompanyId(tempCompanyId);

    setLocationName("");
    setLocationId("");
    setLocationDetail([]);
    setPickupTime("");
    setRateSource("Expedia");
    setResidency("US");
    setDateRange([moment(), moment()]);
    setCalendarValue(null);

    setLorMap({
      "1": false,
      "2": false,
      "3": false,
      "4": false,
      "5": false,
      "6": false,
      Weekly: false,
      Monthly: false,
    });

    setResults([]);
    setSearchText("");
    setFilterDate(null);
    setFilterLor(null);
    setFilterCarClass(null);
    setActiveTab("table");
    setRawResponse(null);

    fetch(
      baseURL + "/get_company_location/" + tempCompanyId
      + "?companyNumber=" + encodeURIComponent(tempCompanyId.toString()),
      {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      }
    )
      .then((res) => res.json())
      .then((data) => setLocationDetail(data?.res || []));
  }, [companyName, companyDetail]);

  useEffect(() => {
    if (!locationName) return;

    const loc = locationDetail.find((l) => l.location_name === locationName);
    if (loc?.location_id) setLocationId(loc.location_id);

    setResults([]);
    setSearchText("");
    setFilterDate(null);
    setFilterLor(null);
    setFilterCarClass(null);
    setActiveTab("table");
    setRawResponse(null);
  }, [locationName, locationDetail]);

  const lorKeys = useMemo(() => Object.keys(lorMap), [lorMap]);
  const selectedLor = useMemo(() => lorKeys.filter((k) => lorMap[k]), [lorKeys, lorMap]);

  const toggleSelectAllLor = () => {
    const allSelected = lorKeys.every((k) => lorMap[k] === true);
    const next = {};
    lorKeys.forEach((k) => {
      next[k] = !allSelected;
    });
    setLorMap(next);
  };

  const onLorGroupChange = (checkedValues) => {
    const next = {};
    lorKeys.forEach((k) => {
      next[k] = checkedValues.includes(k);
    });
    setLorMap(next);
  };

  const companyOptions = useMemo(
    () => companyDetail.map((c) => ({ value: c.company_name, label: c.company_name })),
    [companyDetail]
  );

  const locationOptions = useMemo(
    () =>
      locationDetail.map((l) => ({
        value: l.location_name,
        label:
          l.location_name && l.location_id
            ? `${l.location_name} - ${String(l.location_id).trim().split("-")[1]}`
            : l.location_name,
      })),
    [locationDetail]
  );

  const normalizedResults = useMemo(() => {
    if (!results || results.length === 0) return [];
    return results.map((row, idx) => normalizeRow(row, idx));
  }, [results]);

  const pickupDateOptions = useMemo(() => {
    const s = new Set(normalizedResults.map((x) => String(x.pickup_date)).filter((v) => v && v !== "-"));
    return Array.from(s).sort().map((d) => ({ value: d, label: d }));
  }, [normalizedResults]);

  const lorOptions = useMemo(() => {
    const s = new Set(normalizedResults.map((x) => x.lor).filter((v) => v && v !== "-"));
    return Array.from(s)
      .sort((a, b) => getLorSortValue(a) - getLorSortValue(b))
      .map((l) => ({ value: l, label: String(l) }));
  }, [normalizedResults]);

  const carClassOptions = useMemo(() => {
    const s = new Set(normalizedResults.map((x) => x.carClass).filter((v) => v && v !== "-"));
    return Array.from(s).sort().map((c) => ({ value: c, label: c }));
  }, [normalizedResults]);

  const filteredResults = useMemo(() => {
    const tokens = (searchText || "")
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    return normalizedResults.filter((r) => {
      const matchesTokens =
        tokens.length === 0 ||
        tokens.every((t) => {
          return (
            String(r.pickup_date || "").toLowerCase().includes(t) ||
            String(r.lor || "").toLowerCase().includes(t) ||
            String(r.carClass || "").toLowerCase().includes(t)
          );
        });

      const matchesDate = !filterDate || String(r.pickup_date) === filterDate;
      const matchesLor = filterLor === null || filterLor === undefined || String(r.lor) === String(filterLor);
      const matchesCar = !filterCarClass || r.carClass === filterCarClass;

      return matchesTokens && matchesDate && matchesLor && matchesCar;
    });
  }, [normalizedResults, searchText, filterDate, filterLor, filterCarClass]);

  const stats = useMemo(() => {
    const rows = filteredResults;

    const diffs = rows
      .map((x) => {
        const val = Number(x.higherPct);
        return Number.isFinite(val) ? val : null;
      })
      .filter((n) => n !== null);

    const avg = diffs.length ? diffs.reduce((a, b) => a + b, 0) / diffs.length : null;
    const min = diffs.length ? Math.min(...diffs) : null;
    const max = diffs.length ? Math.max(...diffs) : null;

    const uniqDates = new Set(rows.map((x) => String(x.pickup_date)).filter((v) => v && v !== "-")).size;
    const uniqLors = new Set(rows.map((x) => x.lor).filter((v) => v && v !== "-")).size;
    const uniqCars = new Set(rows.map((x) => x.carClass).filter((v) => v && v !== "-")).size;

    return { avg, min, max, uniqDates, uniqLors, uniqCars, count: rows.length };
  }, [filteredResults]);

  const makeFileBaseName = () => {
    const [fromM, toM] = dateRange || [];
    const fromStr = fromM ? fromM.format("YYYYMMDD") : "from";
    const toStr = toM ? toM.format("YYYYMMDD") : "to";
    const loc = locationName ? locationName.replace(/\s+/g, "_") : "location";
    return `rate_automation_${loc}_${fromStr}_${toStr}`;
  };

  const exportExcel = () => {
    if (!filteredResults.length) return message.warning("No results to download.");

    const rows = filteredResults.map((r) => ({
      "Pickup Date": String(r.pickup_date ?? "-"),
      LOR: String(r.lor ?? "-"),
      "Car Class": String(r.carClass ?? "-"),
      "Routes Rate": Number(r.routesRate).toFixed(2),
      "OTA Displaying Rate": Number(r.expediaRate).toFixed(2),
      "% Higher": Number(r.higherPct).toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Results");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `${makeFileBaseName()}.xlsx`);
    message.success("Excel file downloaded!");
  };

  const exportPDF = () => {
    if (!filteredResults.length) return message.warning("No results to download.");

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text("MarkUp Results", 40, 30);

    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Account: ${companyName || "-"}    Location: ${locationName || "-"}`, 40, 50);

    const [fromM, toM] = dateRange || [];
    doc.text(
      `Date Range: ${fromM ? fromM.format("DD-MM-YYYY") : "-"} to ${toM ? toM.format("DD-MM-YYYY") : "-"}`,
      40,
      65
    );

    doc.text(`Rate Source: ${rateSource || "-"}    Pickup Time: ${pickupTime || "-"}`, 40, 80);

    const tableRows = filteredResults.map((r) => [
      String(r.pickup_date ?? ""),
      String(r.lor ?? ""),
      String(r.carClass ?? ""),
      Number(r.routesRate ?? 0).toFixed(2),
      Number(r.expediaRate ?? 0).toFixed(2),
      `${Number(r.higherPct ?? 0).toFixed(2)}%`,
    ]);

    autoTable(doc, {
      head: [["Pickup Date", "LOR", "Car Class", "Routes Rate", "OTA Displaying Rate", "% Higher"]],
      body: tableRows,
      startY: 100,
      styles: { fontSize: 9, cellPadding: 5, halign: "center", fillColor: [245, 245, 245] },
      headStyles: { fillColor: [22, 119, 255], textColor: 255, halign: "center", fontStyle: "bold" },
      columnStyles: {
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
    });

    doc.save(`${makeFileBaseName()}.pdf`);
    message.success("PDF file downloaded!");
  };

  const handleSubmit = async () => {
    if (!companyName) return message.error("Please select Account Name");
    if (!locationName) return message.error("Please select Location Name");
    if (!pickupTime) return message.error("Please select PickUp Time");
    if (selectedLor.length === 0) return message.error("Please select at least one LOR");

    const [fromMoment, toMoment] = dateRange || [];
    if (!fromMoment || !toMoment) return message.error("Please select Pickup Date Range");

    if (rateSource === "DiscoverCars" && !residency) {
      return message.error("Please select Country of Residence (DiscoverCars)");
    }

    const fromDate = fromMoment.toDate();
    const toDate = toMoment.toDate();

    const days = Math.floor((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
    const timeDurationCount = 0.9 * days * selectedLor.length;
    let durationValue = timeDurationCount * 40000;
    if (rateSource === "DiscoverCars") durationValue = timeDurationCount * 80000;

    setLoading(true);
    setSubmitting(true);
    setButtonDisabled(true);
    setProgressDuration(durationValue);

    setResults([]);
    setSearchText("");
    setFilterDate(null);
    setFilterLor(null);
    setFilterCarClass(null);
    setActiveTab("table");
    setRawResponse(null);

    const payload = {
      companyNumber: String(companyId),
      location_name: locationName,
      account_id: String(companyId),
      location_id: String(locationId),
      pickup_time: pickupTime,
      rateSourceSelected: rateSource,
      ...(rateSource === "DiscoverCars" ? { residencySelected: residency } : {}),
      competitionSelected: "SIPP CODE",
      paritySelected: "Static",
      lor: lorMap,
      from_date: new Date(fromDate).toDateString(),
      to_date: new Date(toDate).toDateString(),
      created_by: loggedInUserDetail?.userEmail,
      updated_by: loggedInUserDetail?.userEmail,
      created_date: new Date(),
      updated_date: new Date(),
    };

    try {
      const res = await axios.post(baseURL + "/save_markup_info", payload);

      console.log("FULL AXIOS RESPONSE:", res);
      console.log("RESPONSE STATUS:", res?.status);
      console.log("RESPONSE HEADERS:", res?.headers);
      console.log("RESPONSE DATA:", res?.data);

      const data = res?.data;
      setRawResponse(data);

      const normalizedData = normalizeResponseData(data);
      console.log("NORMALIZED DATA:", normalizedData);

      if (normalizedData && normalizedData.length > 0) {
        setResults(normalizedData);
        message.success(`✅ Completed! ${normalizedData.length} rows found.`);
      } else {
        message.warning("⚠️ Response received but no data found. Check Debug tab for details.");
        setResults([]);
      }
    } catch (err) {
      console.error("❌ Error object:", err);
      console.error("❌ Error response:", err?.response);
      console.error("❌ Error response data:", err?.response?.data);
      console.error("❌ Error message:", err?.message);
      message.error("Failed to submit MarkUp Criteria. Please try again.");
    } finally {
      setProgressDuration(0);
      setLoading(false);
      setSubmitting(false);
      setButtonDisabled(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: <div style={{ textAlign: "center", fontWeight: "600", color: "#1677ff" }}>📅 Pickup Date</div>,
        dataIndex: "pickup_date",
        key: "pickup_date",
        align: "center",
        width: 130,
        sorter: (a, b) => String(a.pickup_date || "").localeCompare(String(b.pickup_date || "")),
        render: (v) => {
          const text = v === undefined || v === null || v === "" ? "-" : String(v);
          return text !== "-" ? <Tag color="blue" style={{ fontSize: 12 }}>{text}</Tag> : "-";
        },
      },
      {
        title: <div style={{ textAlign: "center", fontWeight: "600", color: "#1677ff" }}>📊 LOR</div>,
        dataIndex: "lor",
        key: "lor",
        align: "center",
        width: 90,
        sorter: (a, b) => getLorSortValue(a.lor) - getLorSortValue(b.lor),
        render: (v) => (
          <Tag color="geekblue" style={{ fontSize: 12, fontWeight: "600" }}>
            {String(v ?? "-")}
          </Tag>
        ),
      },
      {
        title: <div style={{ textAlign: "center", fontWeight: "600", color: "#1677ff" }}>🚗 SIPP Code</div>,
        dataIndex: "carClass",
        key: "carClass",
        align: "center",
        width: 100,
        sorter: (a, b) => String(a.carClass || "").localeCompare(String(b.carClass || "")),
        render: (v) => (
          <Tag color="cyan" style={{ fontSize: 12, fontWeight: "500" }}>
            {String(v ?? "-")}
          </Tag>
        ),
      },
      {
        title: <div style={{ textAlign: "center", fontWeight: "600", color: "#1677ff" }}>💰 Routes Rate</div>,
        dataIndex: "routesRate",
        key: "routesRate",
        align: "center",
        width: 110,
        sorter: (a, b) => Number(a.routesRate || 0) - Number(b.routesRate || 0),
        render: (v) => {
          const num = Number(v);
          return (
            <div style={{ textAlign: "center", fontWeight: "500", color: "#237804", whiteSpace: "nowrap" }}>
              {Number.isFinite(num) ? num.toFixed(2) : "-"}
            </div>
          );
        },
      },
      {
        title: <div style={{ textAlign: "center", fontWeight: "600", color: "#1677ff" }}>🏨 OTA Displaying Rate</div>,
        dataIndex: "expediaRate",
        key: "expediaRate",
        align: "center",
        width: 130,
        sorter: (a, b) => Number(a.expediaRate || 0) - Number(b.expediaRate || 0),
        render: (v) => {
          const num = Number(v);
          return (
            <div style={{ textAlign: "center", fontWeight: "500", color: "#1677ff", whiteSpace: "nowrap" }}>
              {Number.isFinite(num) ? num.toFixed(2) : "-"}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: "center", fontWeight: "600", color: "#1677ff" }}>
            📈 % Higher
            <Tooltip title="Percentage difference between OTA Displaying and Routes rates">
              <span style={{ marginLeft: 6, cursor: "help", fontSize: 12 }}>ⓘ</span>
            </Tooltip>
          </div>
        ),
        dataIndex: "higherPct",
        key: "higherPct",
        align: "center",
        width: 100,
        sorter: (a, b) => Number(a.higherPct || 0) - Number(b.higherPct || 0),
        render: (v) => {
          const n = Number(v);
          if (!Number.isFinite(n)) return "-";

          let color = "green";
          let icon = "✓";

          if (n >= 18) {
            color = "red";
            icon = "⚠️";
          } else if (n >= 15) {
            color = "orange";
            icon = "⚡";
          }

          return (
            <Tag color={color} style={{ fontSize: 12, fontWeight: "600", whiteSpace: "nowrap" }}>
              {icon} {n.toFixed(2)}%
            </Tag>
          );
        },
      },
    ],
    []
  );

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100%",
        padding: pagePadding,
      }}
    >
      <div style={{ width: "100%", maxWidth: "none", margin: 0 }}>
        <div style={{ marginBottom: isMobile ? 20 : 32 }}>
          <div style={{ overflowX: "auto" }}>
            <Breadcrumb items={[{ title: "Dashboard" }, { title: "Mark-Up Info" }]} />
          </div>

          <Title
            level={titleLevel}
            style={{
              marginTop: 16,
              marginBottom: 8,
              color: "#1f2937",
              fontWeight: "700",
              lineHeight: 1.2,
            }}
          >
            🚀 Mark-Up Info (PP NON INCL RATE CODE)
          </Title>

          <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14, display: "block" }}>
            Advanced markup calculation with intelligent filtering and analytics
          </Text>
        </div>

        <Card
          style={{
            borderRadius: 16,
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            marginBottom: 24,
            border: "1px solid #e5e7eb",
          }}
          bodyStyle={{ padding: cardPadding }}
        >
          <Form layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item label={<span style={{ fontWeight: "600" }}>🏢 Account Name</span>} required>
                  <Select
                    showSearch
                    placeholder="Select your account..."
                    options={companyOptions}
                    value={companyName || undefined}
                    onChange={(val) => setCompanyName(val)}
                    optionFilterProp="label"
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item label={<span style={{ fontWeight: "600" }}>📍 Location Name</span>} required>
                  <Select
                    showSearch
                    placeholder="Select location..."
                    options={locationOptions}
                    value={locationName || undefined}
                    onChange={(val) => setLocationName(val)}
                    optionFilterProp="label"
                    disabled={!companyName}
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label={<span style={{ fontWeight: "600" }}>⏰ PickUp Time</span>} required>
                  <Select
                    placeholder="Select time..."
                    options={hours.map((h) => ({ value: h, label: h }))}
                    value={pickupTime || undefined}
                    onChange={(val) => setPickupTime(val)}
                    showSearch
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label={<span style={{ fontWeight: "600" }}>📊 Rate Source</span>} required>
                  <Segmented
                    options={rateSourceOptions}
                    value={rateSource}
                    onChange={(val) => setRateSource(val)}
                    block
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              {rateSource === "DiscoverCars" && (
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={<span style={{ fontWeight: "600" }}>🌍 Country of Residence</span>}
                    required
                    tooltip="Required for DiscoverCars"
                  >
                    <Select
                      options={residencyOptions}
                      value={residency}
                      onChange={(val) => setResidency(val)}
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select country..."
                      size="large"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} md={12}>
                <Form.Item label={<span style={{ fontWeight: "600" }}>📅 Pickup Date Range</span>} required>
                  <RangePicker
                    value={calendarValue || dateRange}
                    format="DD-MM-YYYY"
                    allowClear={false}
                    disabledDate={disabledDate}
                    onOpenChange={(open) => {
                      if (open) setCalendarValue(null);
                    }}
                    onCalendarChange={(vals) => setCalendarValue(vals)}
                    onChange={(vals) => {
                      if (!vals || !vals[0] || !vals[1]) return;
                      setDateRange(vals);
                      setCalendarValue(null);
                    }}
                    style={{ width: "100%" }}
                    size="large"
                  />
                  <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                    Start date cannot be in the past. End date is limited to <b>start + 6 days</b>.
                  </Text>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={<span style={{ fontWeight: "600" }}>📈 Length of Rental (LOR)</span>} required>
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Checkbox
                      checked={lorKeys.every((k) => lorMap[k] === true)}
                      indeterminate={selectedLor.length > 0 && selectedLor.length < lorKeys.length}
                      onChange={toggleSelectAllLor}
                      style={{ fontWeight: "500" }}
                    >
                      Select All
                    </Checkbox>

                    <Checkbox.Group
                      value={selectedLor}
                      onChange={onLorGroupChange}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "repeat(3, minmax(0, 1fr))"
                          : "repeat(auto-fit, minmax(90px, 1fr))",
                        gap: 8,
                        width: "100%",
                      }}
                      options={lorKeys.map((k) => ({
                        label: <span style={{ fontWeight: "500" }}>{k}</span>,
                        value: k,
                      }))}
                    />
                  </Space>
                </Form.Item>
              </Col>
            </Row>

            {loading && (
              <Alert
                style={{ marginTop: 16, borderRadius: 12 }}
                type="info"
                icon={<LoadingOutlined style={{ fontSize: 18 }} />}
                message="Fetching Markup commission in Progress"
                description={
                  <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
                    <Progress
                      percent={Math.min(99, Math.max(10, Math.floor(progressDuration / 100000)))}
                      strokeColor={{ "0%": "#1677ff", "100%": "#52c41a" }}
                      size="small"
                    />
                    <Text>Please don't close this screen. Processing your request...</Text>
                  </Space>
                }
              />
            )}

            <div style={{ display: "flex", justifyContent: isMobile ? "stretch" : "flex-end", marginTop: 24 }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={buttonDisabled}
                loading={loading}
                size="large"
                block={buttonBlock}
                icon={<CheckCircleOutlined />}
                style={{
                  height: 44,
                  fontSize: 16,
                  fontWeight: "600",
                  borderRadius: 8,
                  minWidth: isMobile ? "100%" : 180,
                }}
              >
                {loading ? "Processing..." : "Submit Search Criteria"}
              </Button>
            </div>
          </Form>
        </Card>

        {results.length > 0 && (
          <Card
            style={{
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
              border: "1px solid #e5e7eb",
            }}
            bodyStyle={{ padding: cardPadding }}
          >
            <div style={{ marginBottom: 28 }}>
              <Title level={4} style={{ marginBottom: 16, color: "#1f2937", fontWeight: "700" }}>
                📊 Analytics Dashboard
              </Title>

              <Row gutter={[12, 12]}>
                <Col xs={12} sm={8} lg={4}>
                  <Card style={{ borderRadius: 12, textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                    <Statistic
                      title="Total Rows"
                      value={stats.count}
                      valueStyle={{ color: "#1677ff", fontSize: isMobile ? 22 : 28, fontWeight: "700" }}
                    />
                  </Card>
                </Col>

                <Col xs={12} sm={8} lg={4}>
                  <Card style={{ borderRadius: 12, textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                    <Statistic
                      title="Pickup Dates"
                      value={stats.uniqDates}
                      valueStyle={{ color: "#52c41a", fontSize: isMobile ? 22 : 28, fontWeight: "700" }}
                    />
                  </Card>
                </Col>

                <Col xs={12} sm={8} lg={4}>
                  <Card style={{ borderRadius: 12, textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                    <Statistic
                      title="LORs"
                      value={stats.uniqLors}
                      valueStyle={{ color: "#faad14", fontSize: isMobile ? 22 : 28, fontWeight: "700" }}
                    />
                  </Card>
                </Col>

                <Col xs={12} sm={8} lg={4}>
                  <Card style={{ borderRadius: 12, textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                    <Statistic
                      title="SIPP Codes"
                      value={stats.uniqCars}
                      valueStyle={{ color: "#eb2f96", fontSize: isMobile ? 22 : 28, fontWeight: "700" }}
                    />
                  </Card>
                </Col>

                <Col xs={12} sm={8} lg={4}>
                  <Card style={{ borderRadius: 12, textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                    <Statistic
                      title="Avg % Higher"
                      value={stats.avg === null ? "-" : Number(stats.avg).toFixed(2)}
                      suffix={stats.avg === null ? "" : "%"}
                      valueStyle={{ color: "#f5222d", fontSize: isMobile ? 22 : 28, fontWeight: "700" }}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={8} lg={4}>
                  <Card style={{ borderRadius: 12, textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                    <Statistic
                      title="Range"
                      value={
                        stats.min === null || stats.max === null
                          ? "-"
                          : `${Number(stats.min).toFixed(2)}% - ${Number(stats.max).toFixed(2)}%`
                      }
                      valueStyle={{ color: "#722ed1", fontSize: isMobile ? 18 : 22, fontWeight: "700" }}
                    />
                  </Card>
                </Col>
              </Row>
            </div>

            <Divider style={{ margin: "24px 0" }} />

            <div style={{ marginBottom: 24 }}>
              <Title level={4} style={{ marginBottom: 16, color: "#1f2937", fontWeight: "700" }}>
                🔍 Advanced Search & Filters
              </Title>

              <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                <Col xs={24} lg={10}>
                  <Input
                    placeholder="🔎 Search: Date, LOR, or SIPP Code (e.g., 2026-04-21 lor 2 CDAR)"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    prefix={<SearchOutlined style={{ color: "#1677ff" }} />}
                    size="large"
                    style={{ borderRadius: 8 }}
                  />
                </Col>

                <Col xs={24} sm={8} lg={4}>
                  <Select
                    placeholder="📅 Pickup Date"
                    options={pickupDateOptions}
                    value={filterDate || undefined}
                    onChange={(v) => setFilterDate(v)}
                    allowClear
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Col>

                <Col xs={24} sm={8} lg={4}>
                  <Select
                    placeholder="📊 LOR"
                    options={lorOptions}
                    value={filterLor ?? undefined}
                    onChange={(v) => setFilterLor(v)}
                    allowClear
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Col>

                <Col xs={24} sm={8} lg={6}>
                  <Select
                    placeholder="🚗 SIPP Code"
                    options={carClassOptions}
                    value={filterCarClass || undefined}
                    onChange={(v) => setFilterCarClass(v)}
                    allowClear
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Col>
              </Row>

              <div
                style={{
                  display: "flex",
                  justifyContent: isMobile ? "stretch" : "flex-end",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  icon={<ClearOutlined />}
                  onClick={() => {
                    setSearchText("");
                    setFilterDate(null);
                    setFilterLor(null);
                    setFilterCarClass(null);
                  }}
                  style={{ borderRadius: 8, flex: isMobile ? "1 1 100%" : "unset" }}
                >
                  Clear All
                </Button>

                <Button
                  icon={<FileExcelOutlined />}
                  onClick={exportExcel}
                  style={{ borderRadius: 8, flex: isMobile ? "1 1 100%" : "unset" }}
                  type="primary"
                  ghost
                >
                  Download Excel
                </Button>

                <Button
                  icon={<FilePdfOutlined />}
                  onClick={exportPDF}
                  style={{ borderRadius: 8, flex: isMobile ? "1 1 100%" : "unset" }}
                  danger
                  ghost
                >
                  Download PDF
                </Button>

                <Button
                  type={debugMode ? "primary" : "default"}
                  onClick={() => setDebugMode(!debugMode)}
                  icon={<ExclamationCircleOutlined />}
                  style={{ borderRadius: 8, flex: isMobile ? "1 1 100%" : "unset" }}
                >
                  {debugMode ? "Hide Debug" : "Debug Info"}
                </Button>
              </div>
            </div>

            <Divider style={{ margin: "24px 0" }} />

            {debugMode && rawResponse && (
              <Alert
                type="info"
                message="🔍 Debug Information"
                description={
                  <div style={{ maxHeight: 300, overflow: "auto" }}>
                    <pre style={{ margin: 0, fontSize: 11, color: "#0b1020", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {JSON.stringify(rawResponse, null, 2)}
                    </pre>
                  </div>
                }
                style={{ marginBottom: 16, borderRadius: 8 }}
                closable={false}
              />
            )}

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: "table",
                  label: (
                    <span style={{ fontWeight: "500" }}>
                      📋 Results Table{" "}
                      <Badge count={filteredResults.length} style={{ backgroundColor: "#52c41a" }} />
                    </span>
                  ),
                  children: (
                    <div style={{ marginTop: 16 }}>
                      {filteredResults.length > 0 ? (
                        <div style={{ width: "100%", overflowX: "auto" }}>
                          <Table
                            columns={columns}
                            dataSource={filteredResults}
                            size={isMobile ? "small" : "middle"}
                            bordered
                            locale={{ emptyText: <Empty description="No matching results found." /> }}
                            pagination={{
                              pageSize: 15,
                              showSizeChanger: true,
                              pageSizeOptions: [10, 15, 20, 50, 100],
                              responsive: true,
                              showTotal: (total, range) => (
                                <Text type="secondary">
                                  Showing {range[0]} to {range[1]} of {total} results
                                </Text>
                              ),
                              style: { marginTop: 16 },
                            }}
                            rowKey="key"
                            sticky={!isMobile}
                            rowClassName={(record, index) => (index % 2 === 0 ? "row-even" : "row-odd")}
                            scroll={{ x: 900 }}
                          />
                        </div>
                      ) : (
                        <Empty description="No data to display" style={{ marginTop: 50 }} />
                      )}
                    </div>
                  ),
                },
                {
                  key: "raw",
                  label: <span style={{ fontWeight: "500" }}>🔗 Raw JSON</span>,
                  children: (
                    <pre
                      style={{
                        margin: 16,
                        background: "#1f2937",
                        color: "#10b981",
                        padding: 16,
                        borderRadius: 12,
                        overflow: "auto",
                        fontSize: 12,
                        fontFamily: "Menlo, monospace",
                        maxHeight: 500,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {JSON.stringify(results, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          </Card>
        )}

        {!loading && results.length === 0 && submitting === false && (
          <Card
            style={{
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
              textAlign: "center",
              padding: isMobile ? 20 : 60,
              border: "2px dashed #d9d9d9",
            }}
          >
            <Empty
              description={<Text style={{ fontSize: isMobile ? 14 : 16 }}>Submit the form to see results</Text>}
              style={{ marginTop: 20 }}
            />
          </Card>
        )}
      </div>

      <style>{`
        .row-even {
          background-color: #fafafa;
        }
        .row-odd {
          background-color: #ffffff;
        }
        .ant-table-row:hover {
          background-color: #f0f5ff !important;
        }

        @media (max-width: 767px) {
          .ant-statistic {
            overflow: hidden;
          }

          .ant-statistic-content {
            white-space: normal !important;
            word-break: break-word;
          }

          .ant-breadcrumb {
            white-space: nowrap;
          }

          .ant-tabs-tab-btn {
            font-size: 13px;
          }

          .ant-card-body {
            overflow-x: hidden;
          }
        }
      `}</style>
    </div>
  );
}
